import React, { useState } from 'react'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { AvatarMediaPreview } from 'AdminPanel/SpineAvatars/SpineAvatars/components/AvatarMediaPreview'
import { X } from 'lucide-react'

export interface MyMediaProps {
  /** 无障碍 / 弹窗标题 */
  alt: string
  /** 缩略图容器 class（尺寸、圆角、边框等） */
  className?: string
  /** 列表缩略视频地址（可用低清 preview） */
  videoUrl?: string
  /** 弹窗播放地址；不传则用 videoUrl（对齐 MyImage.previewSrc） */
  previewVideoUrl?: string
  /** 视频失败或未就绪时的封面图 */
  imageUrl?: string
  /** 是否允许点击放大预览，默认 true */
  preview?: boolean
  /** 列表懒加载视频，默认 false（表格缩略图常需立即播） */
  lazyVideo?: boolean
  videoFit?: 'contain' | 'cover'
}

/**
 * MyMedia - 视频/图混合缩略图，支持点击弹窗放大播放（对齐 MyImage）
 */
export function MyMedia({
  alt,
  className,
  videoUrl,
  previewVideoUrl,
  imageUrl,
  preview = true,
  lazyVideo = false,
  videoFit = 'contain',
}: MyMediaProps) {
  const [open, setOpen] = useState(false)
  const thumbVideo = videoUrl?.trim() || undefined
  const dialogVideo = (previewVideoUrl || videoUrl)?.trim() || undefined

  if (!thumbVideo && !imageUrl) {
    return null
  }

  return (
    <>
      <button
        type="button"
        className={cn(
          'block overflow-hidden bg-black p-0',
          preview ? 'cursor-zoom-in' : 'cursor-default',
          className,
        )}
        onClick={() => {
          if (preview) setOpen(true)
        }}
        aria-label={`${alt} 预览`}
        disabled={!preview}
      >
        <AvatarMediaPreview
          videoUrl={thumbVideo}
          imageUrl={imageUrl}
          lazyVideo={lazyVideo}
          videoFit={videoFit}
          className="h-full w-full"
        />
      </button>
      {preview && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="h-fit w-fit max-h-[95vh] max-w-[95vw] border-none bg-transparent p-0 shadow-none outline-none [&>button]:hidden">
            <DialogTitle className="sr-only">{alt}</DialogTitle>
            <DialogDescription className="sr-only">
              {alt} 视频预览
            </DialogDescription>
            <div className="relative group">
              <div className="aspect-square w-[min(90vw,480px)] overflow-hidden rounded-md bg-black">
                <AvatarMediaPreview
                  videoUrl={dialogVideo}
                  imageUrl={imageUrl}
                  lazyVideo={false}
                  videoFit={videoFit}
                  className="h-full w-full"
                />
              </div>
              <DialogClose className="absolute right-2 top-2 rounded-full border bg-background/80 p-1 shadow-sm backdrop-blur-sm transition-colors hover:bg-accent">
                <X className="h-4 w-4" />
                <span className="sr-only">关闭</span>
              </DialogClose>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

export default MyMedia
